// Estilo visual da empresa: tokens do tema (globals.css) e a intensidade das
// cores dos indicadores, escolhidos pelo super admin em /estilos-visuais.
//
// Os indicadores coloridos usam a paleta do Tailwind direto (`text-emerald-600`,
// `bg-amber-500/12`...), em mais de mil lugares. No Tailwind v4 cada uma dessas
// classes lê uma variável (`--color-emerald-600`), então trocar a variável
// troca a cor em todas as telas sem tocar em componente nenhum.

// Converte "#38bdf8" no formato das variáveis do tema: "H S% L%".
export const hexParaHsl = (hex) => {
	const limpo = String(hex || "").replace("#", "").trim();

	if (!/^[0-9a-fA-F]{6}$/.test(limpo)) return null;

	const r = parseInt(limpo.slice(0, 2), 16) / 255;
	const g = parseInt(limpo.slice(2, 4), 16) / 255;
	const b = parseInt(limpo.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;

	if (max === min) return `0 0% ${Math.round(l * 100)}%`;

	const d = max - min;
	const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

	let h;
	if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
	else if (max === g) h = ((b - r) / d + 2) / 6;
	else h = ((r - g) / d + 4) / 6;

	return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// O caminho de volta, para o <input type="color">.
export const hslParaHex = (hsl) => {
	const m = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(String(hsl || ""));
	if (!m) return "#000000";

	const h = Number(m[1]) / 360;
	const s = Number(m[2]) / 100;
	const l = Number(m[3]) / 100;
	const canal = (n) => {
		const k = (n + h * 12) % 12;
		const a = s * Math.min(l, 1 - l);
		const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
		return Math.round(v * 255).toString(16).padStart(2, "0");
	};

	return `#${canal(0)}${canal(8)}${canal(4)}`;
};

// O que o super admin edita, agrupado como aparece na tela. A cor primária
// não está aqui de propósito: ela é da marca da empresa, e a marca vence.
export const GRUPOS_DE_TOKENS = [
	{
		titulo: "Superfícies",
		tokens: [
			{ chave: "background", nome: "Fundo da página" },
			{ chave: "card", nome: "Fundo dos cartões" },
			{ chave: "popover", nome: "Menus e janelas" },
			{ chave: "muted", nome: "Faixas e áreas apagadas" },
			{ chave: "secondary", nome: "Botão secundário" },
			{ chave: "accent", nome: "Item sob o mouse" },
		],
	},
	{
		titulo: "Bordas",
		tokens: [
			{ chave: "border", nome: "Bordas dos cartões e divisórias" },
			{ chave: "input", nome: "Borda dos campos" },
		],
	},
	{
		titulo: "Texto",
		tokens: [
			{ chave: "foreground", nome: "Texto principal" },
			{ chave: "muted-foreground", nome: "Texto secundário" },
		],
	},
	{
		titulo: "Erro",
		tokens: [{ chave: "destructive", nome: "Erro e exclusão" }],
	},
];

// Texto sobre cartão, menu e botão acompanha o texto principal quando o
// estilo não diz nada — senão trocar o texto principal deixaria metade da
// tela na cor antiga.
const DERIVADOS = {
	"card-foreground": "foreground",
	"popover-foreground": "foreground",
	"secondary-foreground": "foreground",
	"accent-foreground": "foreground",
};

// "Padrão" é a sombra difusa que todo cartão já tem (globals.css, "Sombra
// difusa de cartão") — os valores aqui são cópia dela só para a prévia.
const SOMBRA_PADRAO = {
	claro: "0 10px 30px -14px rgb(67 89 113 / 0.25)",
	escuro: "0 10px 30px -14px rgb(0 0 0 / 0.6)",
};

export const SOMBRAS = [
	{ id: "", nome: "Padrão", claro: "", escuro: "" },
	{ id: "nenhuma", nome: "Sem sombra", claro: "none", escuro: "none" },
	{
		id: "leve",
		nome: "Leve",
		claro: "0 1px 2px 0 rgb(0 0 0 / 0.05), 0 1px 3px 0 rgb(0 0 0 / 0.08)",
		escuro: "0 1px 3px 0 rgb(0 0 0 / 0.45)",
	},
	{
		id: "media",
		nome: "Média",
		claro: "0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06)",
		escuro: "0 4px 8px -2px rgb(0 0 0 / 0.6)",
	},
];

export const RAIOS = [
	{ valor: "0.25rem", nome: "Retos" },
	{ valor: "0.5rem", nome: "Pouco arredondados" },
	{ valor: "", nome: "Padrão" },
	{ valor: "1rem", nome: "Bem arredondados" },
];

export const FAMILIAS = [
	{ id: "red", nome: "Vermelho" },
	{ id: "orange", nome: "Laranja" },
	{ id: "amber", nome: "Âmbar" },
	{ id: "yellow", nome: "Amarelo" },
	{ id: "lime", nome: "Lima" },
	{ id: "green", nome: "Verde" },
	{ id: "emerald", nome: "Esmeralda" },
	{ id: "teal", nome: "Verde-água" },
	{ id: "cyan", nome: "Ciano" },
	{ id: "sky", nome: "Céu" },
	{ id: "blue", nome: "Azul" },
	{ id: "indigo", nome: "Anil" },
	{ id: "violet", nome: "Violeta" },
	{ id: "purple", nome: "Roxo" },
	{ id: "fuchsia", nome: "Fúcsia" },
	{ id: "pink", nome: "Rosa" },
	{ id: "rose", nome: "Rosa-vermelho" },
];

const TONS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

// Os tokens que a prévia fixa por inteiro (fora a primária, que é da marca).
const TOKENS_DO_TEMA = [
	...GRUPOS_DE_TOKENS.flatMap((g) => g.tokens.map((t) => t.chave)),
	...Object.keys(DERIVADOS),
	"destructive-foreground",
	"radius",
];

// Os valores de fábrica, lidos das próprias regras `:root` e `.dark` da folha
// de estilo — não de `getComputedStyle`, que já devolveria o estilo aplicado
// por cima. Assim não há segunda cópia do globals.css para envelhecer.
let padroes = null;

export const lerPadroes = () => {
	if (padroes) return padroes;

	const claro = {};
	const escuro = {};
	const visitar = (regras) => {
		for (const regra of regras) {
			if (!regra.selectorText) {
				if (regra.cssRules) visitar(regra.cssRules);
				continue;
			}
			const destino =
				regra.selectorText === ".dark"
					? escuro
					: /(^|,\s*):root(,|$)/.test(regra.selectorText)
						? claro
						: null;
			if (!destino) continue;
			for (const prop of regra.style) {
				if (prop.startsWith("--")) {
					destino[prop.slice(2)] = regra.style.getPropertyValue(prop).trim();
				}
			}
		}
	};

	for (const folha of document.styleSheets) {
		try {
			visitar(folha.cssRules);
		} catch {
			// Folha de outra origem (fonte do Google): não dá para ler e não
			// tem tema nenhum.
		}
	}

	const lidos = { claro, escuro: { ...claro, ...escuro } };
	// Lido antes de a folha carregar, tenta de novo na próxima vez.
	if (claro.background) padroes = lidos;
	return lidos;
};

// A paleta do Tailwind v4 é oklch: a intensidade mexe só no croma, então o
// tom continua o mesmo e a claridade também — "Clean" vira cinza do mesmo
// peso, e o texto continua legível sobre o fundo.
const OKLCH = /^oklch\(\s*([\d.]+%?)\s+([\d.]+)\s+([\d.]+)\s*\)$/;

const saturar = (valor, fator) => {
	const m = OKLCH.exec(valor);
	if (!m) return valor;
	const croma = Math.min(0.37, Number(m[2]) * fator);
	return `oklch(${m[1]} ${croma.toFixed(4)} ${m[3]})`;
};

// As variáveis que o estilo define num modo. `completo` fixa também o que o
// estilo não muda (a prévia precisa: ela mora dentro da tela, que pode estar
// com outro estilo aplicado).
export const variaveisDoEstilo = (estilo, escuro, completo = false) => {
	const padrao = lerPadroes()[escuro ? "escuro" : "claro"];
	const tokens = { ...((escuro ? estilo?.escuro : estilo?.claro) || {}) };

	Object.entries(DERIVADOS).forEach(([chave, origem]) => {
		if (!tokens[chave] && tokens[origem]) tokens[chave] = tokens[origem];
	});

	const vars = {};
	if (completo) {
		TOKENS_DO_TEMA.forEach((chave) => {
			if (padrao[chave]) vars[chave] = padrao[chave];
		});
	}
	Object.assign(vars, tokens);
	// A prévia mora dentro da tela: sem isto herdaria a sombra do estilo da
	// empresa de quem está olhando.
	if (completo && !vars["sombra-cartao"]) {
		vars["sombra-cartao"] = SOMBRA_PADRAO[escuro ? "escuro" : "claro"];
	}

	// O Tailwind 4 resolve `--color-card: hsl(var(--card))` uma vez, no
	// `:root`, e o valor desce já calculado: num elemento filho (a prévia),
	// trocar `--card` não pinta nada sem redeclarar o `--color-card` ali. Na
	// raiz não precisa — é o mesmo elemento. (O `.tema-campanha` faz o mesmo
	// no globals.css.)
	if (completo) {
		TOKENS_DO_TEMA.forEach((chave) => {
			if (chave !== "radius") vars[`color-${chave}`] = `hsl(var(--${chave}))`;
		});
	}

	// Os cartões usam `rounded-xl`, que no Tailwind 4 não deriva do
	// `--radius` do tema — sem isto "Cantos" mudaria botões e campos, não os
	// cartões.
	if (vars.radius) {
		vars["radius-xl"] = vars.radius;
		vars["radius-lg"] = vars.radius;
		vars["radius-md"] = `calc(${vars.radius} - 2px)`;
		vars["radius-sm"] = `calc(${vars.radius} - 4px)`;
	}

	const geral = estilo?.paleta?.geral ?? 100;
	const familias = estilo?.paleta?.familias || {};

	FAMILIAS.forEach(({ id }) => {
		const fator = familias[id] ?? geral;
		if (fator === 100 && !completo) return;

		TONS.forEach((tom) => {
			const chave = `color-${id}-${tom}`;
			if (padrao[chave]) vars[chave] = saturar(padrao[chave], fator / 100);
		});
	});

	return vars;
};

// O que foi aplicado em cada elemento, para tirar antes de aplicar outro.
const aplicados = new WeakMap();

export const aplicarEstilo = (el, estilo, escuro, completo = false) => {
	if (!el) return;

	(aplicados.get(el) || []).forEach((prop) => el.style.removeProperty(prop));

	const vars = variaveisDoEstilo(estilo, escuro, completo);
	const props = Object.keys(vars).map((chave) => `--${chave}`);

	Object.entries(vars).forEach(([chave, valor]) =>
		el.style.setProperty(`--${chave}`, valor)
	);
	aplicados.set(el, props);

	// A sombra dos cartões precisa de uma regra no globals.css; a classe
	// liga a regra só quando o estilo pede sombra.
	el.classList.toggle("com-sombra-cartao", !!vars["sombra-cartao"]);
};
